// src/pages/UserMaster-page/UserMaster.js
import React, { useState, useEffect, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
  MdSearch,
  MdEdit,
  MdDelete,
  MdClear,
} from 'react-icons/md';
import {
  FaPlus,
  FaFileExcel,
  FaEye,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaUserShield,
  FaBuilding,
  FaEnvelope,
  FaPhone,
  FaIdCard,
} from 'react-icons/fa';
import { HiOutlineViewGrid, HiOutlineViewList } from 'react-icons/hi';
import BASE_URL from '../../api';

const API_BASE_URL = `${BASE_URL}/api`;

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    '&:hover': { borderColor: '#2563eb' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    fontSize: '0.875rem',
    cursor: 'pointer',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
  }),
};

const initialFormState = {
  username: '',
  password: '',
  address: '',
  phone: '',
  guardianName: '',
  qualification: '',
  dateOfBirth: '',
  gender: '',
  dateofjoining: '',
  aadharCardNumber: '',
  email: '',
  role: '',
  branch: '',
  remarks: '',
  isactive: true,
};

const UserMaster = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [branchFilter, setBranchFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState('card');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [viewUser, setViewUser] = useState(null);

  const token = () => localStorage.getItem('token');
  const headers = () => ({
    'Content-Type': 'application/json',
    ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [uRes, rRes, bRes] = await Promise.all([
        fetch(`${API_BASE_URL}/master/user`, { headers: headers() }),
        fetch(`${API_BASE_URL}/role/roles`, { headers: headers() }),
        fetch(`${API_BASE_URL}/master/branch`, { headers: headers() }),
      ]);
      const uData = await uRes.json();
      const rData = await rRes.json();
      const bData = await bRes.json();

      setUsers(uData.data || []);
      setRoles(rData.data || []);
      setBranches(bData.data || []);
    } catch (err) {
      toast.error('Failed to load user records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token()) fetchAll();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, roleFilter, branchFilter]);

  // Options for react-select
  const roleOptions = useMemo(() => {
    return roles
      .filter((r) => r.roleName?.toLowerCase() !== 'customer')
      .map((r) => ({ value: r._id, label: r.roleName }));
  }, [roles]);

  const branchOptions = useMemo(() => {
    return branches.map((b) => ({
      value: b._id,
      label: `${b.branchname} (${b.branchcode || 'N/A'})`,
    }));
  }, [branches]);

  // Validation
  const validateForm = () => {
    const errors = {};
    if (!form.username?.trim()) errors.username = 'Username is required';
    if (!editingUser && !form.password?.trim()) errors.password = 'Password is required';
    if (!form.email?.trim()) errors.email = 'Email is required';
    if (!form.phone?.trim()) errors.phone = 'Phone number is required';
    if (!form.role) errors.role = 'Role is required';
    if (!form.branch) errors.branch = 'Branch is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenModal = (user = null) => {
    setFormErrors({});
    if (user) {
      setEditingUser(user);
      setForm({
        username: user.username || '',
        password: '',
        address: user.address || '',
        phone: user.phone || '',
        guardianName: user.guardianName || '',
        qualification: user.qualification || '',
        dateOfBirth: user.dateOfBirth ? user.dateOfBirth.split('T')[0] : '',
        gender: user.gender || '',
        dateofjoining: user.dateofjoining ? user.dateofjoining.split('T')[0] : '',
        aadharCardNumber: user.aadharCardNumber || '',
        email: user.email || '',
        role: user.role?._id || user.role || '',
        branch: user.branch?._id || user.branch || '',
        remarks: user.remarks || '',
        isactive: user.isactive ?? true,
      });
    } else {
      setEditingUser(null);
      setForm(initialFormState);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = { ...form };
      if (editingUser && !payload.password) {
        delete payload.password;
      }

      const url = editingUser ? `${API_BASE_URL}/master/user/${editingUser._id}` : `${API_BASE_URL}/master/user`;
      const method = editingUser ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: headers(),
        body: JSON.stringify(payload),
      });

      const resData = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(resData.message || 'Operation failed');
      }

      toast.success(editingUser ? 'User updated successfully!' : 'User created successfully!');
      handleCloseModal();
      fetchAll();
    } catch (err) {
      toast.error(err.message || 'Error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: 'Delete User?',
      text: `Are you sure you want to delete ${name || 'this user'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_BASE_URL}/master/user/${id}`, {
          method: 'DELETE',
          headers: headers(),
        });
        if (!res.ok) throw new Error('Delete failed');
        toast.success('User removed');
        fetchAll();
      } catch (err) {
        toast.error(err.message || 'Failed to delete');
      }
    }
  };

  // Export Excel
  const handleExportExcel = () => {
    if (users.length === 0) {
      toast.info('No user data to export');
      return;
    }
    const exportRows = filteredUsers.map((u, idx) => ({
      'S.No': idx + 1,
      Username: u.username || '-',
      Email: u.email || '-',
      Phone: u.phone || '-',
      Role: u.role?.roleName || '-',
      Branch: u.branch?.branchname || '-',
      Aadhar: u.aadharCardNumber || '-',
      'Joining Date': u.dateofjoining ? u.dateofjoining.split('T')[0] : '-',
      Status: u.isactive ? 'Active' : 'Inactive',
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
    XLSX.writeFile(workbook, `User_Master_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Users exported to Excel successfully!');
  };

  // Filter Logic
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const isActive = u.isactive;
      if (statusFilter === 'ACTIVE' && !isActive) return false;
      if (statusFilter === 'INACTIVE' && isActive) return false;

      if (roleFilter !== 'ALL') {
        const rId = u.role?._id || u.role;
        if (rId !== roleFilter) return false;
      }

      if (branchFilter !== 'ALL') {
        const bId = u.branch?._id || u.branch;
        if (bId !== branchFilter) return false;
      }

      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase().trim();
      return (
        (u.username || '').toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term) ||
        (u.phone || '').includes(term) ||
        (u.role?.roleName || '').toLowerCase().includes(term) ||
        (u.branch?.branchname || '').toLowerCase().includes(term)
      );
    });
  }, [users, searchTerm, statusFilter, roleFilter, branchFilter]);

  const totalItems = filteredUsers.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, currentPage, pageSize]);

  const totalActive = useMemo(() => users.filter((u) => u.isactive).length, [users]);
  const totalInactive = users.length - totalActive;

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header & KPI Summary */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 uppercase tracking-wide flex items-center gap-2">
            User Master
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            Manage system users, employee credentials, role permissions & branch assignments
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-lg text-sm font-semibold transition shadow-sm"
          >
            <FaFileExcel className="text-green-600 text-base" />
            <span>Export Excel</span>
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition shadow-md hover:shadow-lg"
          >
            <FaPlus className="text-sm" />
            <span>Add New User</span>
          </button>
        </div>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Total Users</p>
            <p className="text-2xl font-bold text-gray-800">{users.length}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
            <FaUserShield />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Active Accounts</p>
            <p className="text-2xl font-bold text-green-600">{totalActive}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center font-bold">
            <FaCheckCircle />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Inactive</p>
            <p className="text-2xl font-bold text-gray-500">{totalInactive}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center font-bold">
            <FaTimesCircle />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Roles</p>
            <p className="text-2xl font-bold text-indigo-600">{roles.length}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
            <FaBuilding />
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5 md:p-6 mb-8">
        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4 mb-6 pb-5 border-b border-gray-200">
          <div className="relative flex-1 min-w-[260px]">
            <MdSearch className="absolute left-3.5 top-3 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search by username, email, phone, role, branch..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-3 text-gray-400">
                <MdClear className="text-lg" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>

            {/* Role Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">Role:</span>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 max-w-[150px]"
              >
                <option value="ALL">All Roles</option>
                {roles.map((r) => (
                  <option key={r._id} value={r._id}>{r.roleName}</option>
                ))}
              </select>
            </div>

            {/* Branch Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">Branch:</span>
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 max-w-[150px]"
              >
                <option value="ALL">All Branches</option>
                {branches.map((b) => (
                  <option key={b._id} value={b._id}>{b.branchname}</option>
                ))}
              </select>
            </div>

            {(searchTerm || statusFilter !== 'ALL' || roleFilter !== 'ALL' || branchFilter !== 'ALL') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('ALL');
                  setRoleFilter('ALL');
                  setBranchFilter('ALL');
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-2 py-1 underline"
              >
                Reset
              </button>
            )}

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200 ml-auto lg:ml-2">
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'card' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600'
                }`}
              >
                <HiOutlineViewGrid className="text-base" />
                <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'table' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600'
                }`}
              >
                <HiOutlineViewList className="text-base" />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="py-20 text-center text-gray-400 flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm font-semibold">Loading users...</p>
          </div>
        ) : paginatedData.length === 0 ? (
          <div className="py-16 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <FaUserShield className="text-4xl text-gray-300 mx-auto mb-2" />
            <p className="text-base font-bold text-gray-700">No users found</p>
          </div>
        ) : viewMode === 'card' ? (
          /* Landscape Cards Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
            {paginatedData.map((u, idx) => {
              const isActive = u.isactive;
              const sNo = (currentPage - 1) * pageSize + idx + 1;

              return (
                <div
                  key={u._id || idx}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:border-blue-400 p-5 flex flex-col justify-between transition-all"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-bold text-base flex items-center justify-center shadow-sm">
                          {u.username?.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-gray-900 text-base leading-tight">
                              {u.username}
                            </h3>
                            <span className="text-xs text-gray-400 font-medium">#{sNo}</span>
                          </div>
                          <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 inline-block mt-0.5">
                            {u.role?.roleName || 'No Role'}
                          </span>
                        </div>
                      </div>

                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                          isActive
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-red-50 text-red-600 border-red-200'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
                        {isActive ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>

                    <div className="space-y-1.5 my-3.5 bg-gray-50/70 p-3 rounded-lg border border-gray-100 text-xs">
                      <div className="flex items-center gap-1.5 text-gray-700 truncate">
                        <FaEnvelope className="text-blue-500 flex-shrink-0" />
                        <span className="truncate">{u.email || '-'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-700">
                        <FaPhone className="text-emerald-500 flex-shrink-0" />
                        <span>{u.phone || '-'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-700">
                        <FaBuilding className="text-indigo-500 flex-shrink-0" />
                        <span className="font-semibold text-gray-800">{u.branch?.branchname || '-'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-2">
                    <button
                      onClick={() => setViewUser(u)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                    >
                      <FaEye /> <span>View</span>
                    </button>
                    <button
                      onClick={() => handleOpenModal(u)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition"
                    >
                      <MdEdit /> <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(u._id, u.username)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition"
                    >
                      <MdDelete /> <span>Delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Table List View */
          <div className="overflow-x-auto rounded-lg border border-gray-200 mb-6">
            <table className="w-full text-left text-sm text-gray-600 border-collapse">
              <thead className="bg-blue-600 text-white text-xs uppercase font-semibold">
                <tr>
                  <th className="py-3 px-4 text-center w-14">S.No</th>
                  <th className="py-3 px-4">Username / Email</th>
                  <th className="py-3 px-4">Role</th>
                  <th className="py-3 px-4">Branch</th>
                  <th className="py-3 px-4">Phone</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {paginatedData.map((u, idx) => {
                  const isActive = u.isactive;
                  const sNo = (currentPage - 1) * pageSize + idx + 1;

                  return (
                    <tr key={u._id || idx} className="hover:bg-blue-50/50 transition">
                      <td className="py-3.5 px-4 text-center text-gray-500 font-semibold">{sNo}</td>
                      <td className="py-3.5 px-4">
                        <div className="font-bold text-gray-900">{u.username}</div>
                        <div className="text-xs text-blue-600">{u.email}</div>
                      </td>
                      <td className="py-3.5 px-4 font-semibold text-gray-700">{u.role?.roleName || '-'}</td>
                      <td className="py-3.5 px-4 font-medium text-gray-700">{u.branch?.branchname || '-'}</td>
                      <td className="py-3.5 px-4">{u.phone}</td>
                      <td className="py-3.5 px-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex justify-center items-center gap-1.5">
                          <button onClick={() => setViewUser(u)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                            <FaEye />
                          </button>
                          <button onClick={() => handleOpenModal(u)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded">
                            <MdEdit />
                          </button>
                          <button onClick={() => handleDelete(u._id, u.username)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                            <MdDelete />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1 outline-none font-semibold"
              >
                {[6, 10, 20, 50].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span>entries per page</span>
              <span className="hidden md:inline ml-3 text-gray-400">
                (Page {currentPage} of {totalPages})
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg border font-semibold text-xs transition disabled:opacity-40 hover:bg-gray-100"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((pageNum, idx, arr) => (
                  <React.Fragment key={pageNum}>
                    {idx > 0 && pageNum - arr[idx - 1] > 1 && <span className="px-1 text-gray-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-xs font-bold transition ${
                        currentPage === pageNum ? 'bg-blue-600 text-white' : 'border text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  </React.Fragment>
                ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-3 py-1.5 rounded-lg border font-semibold text-xs transition disabled:opacity-40 hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Add/Edit User */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden animate-fadeIn">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FaUserShield className="text-xl" />
                <h3 className="text-xl font-bold">{editingUser ? 'Edit User' : 'Add New User'}</h3>
              </div>
              <button onClick={handleCloseModal} className="text-white/80 hover:text-white p-1 rounded-full">
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-6 flex-1">
              {/* Basic Info */}
              <div className="bg-gray-50/80 p-5 rounded-xl border border-gray-200">
                <h4 className="text-xs font-bold text-blue-700 uppercase mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-4 bg-blue-600 rounded-full"></span> Account Credentials
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Username <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={form.username}
                      onChange={(e) => setForm({ ...form, username: e.target.value })}
                      placeholder="Username"
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.username ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.username && <p className="text-red-500 text-xs mt-1">{formErrors.username}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      {editingUser ? 'New Password (Optional)' : 'Password *'}
                    </label>
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="******"
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.password ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="email@example.com"
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="10 digit phone"
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                  </div>

                  {/* Searchable Role */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={roleOptions}
                      value={roleOptions.find((r) => r.value === form.role) || null}
                      onChange={(opt) => setForm({ ...form, role: opt ? opt.value : '' })}
                      placeholder="Search / select role..."
                      styles={customSelectStyles}
                    />
                    {formErrors.role && <p className="text-red-500 text-xs mt-1">{formErrors.role}</p>}
                  </div>

                  {/* Searchable Branch */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Branch <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={branchOptions}
                      value={branchOptions.find((b) => b.value === form.branch) || null}
                      onChange={(opt) => setForm({ ...form, branch: opt ? opt.value : '' })}
                      placeholder="Search / select branch..."
                      styles={customSelectStyles}
                    />
                    {formErrors.branch && <p className="text-red-500 text-xs mt-1">{formErrors.branch}</p>}
                  </div>
                </div>
              </div>

              {/* Personal Info */}
              <div className="bg-gray-50/80 p-5 rounded-xl border border-gray-200">
                <h4 className="text-xs font-bold text-blue-700 uppercase mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-4 bg-blue-600 rounded-full"></span> Personal Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Aadhar Number</label>
                    <input
                      value={form.aadharCardNumber}
                      onChange={(e) => setForm({ ...form, aadharCardNumber: e.target.value })}
                      placeholder="12 digit aadhar"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">DOB</label>
                    <input
                      type="date"
                      value={form.dateOfBirth}
                      onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Joining Date</label>
                    <input
                      type="date"
                      value={form.dateofjoining}
                      onChange={(e) => setForm({ ...form, dateofjoining: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Gender</label>
                    <select
                      value={form.gender}
                      onChange={(e) => setForm({ ...form, gender: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Address</label>
                    <input
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="Residential address"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="md:col-span-3 flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      id="user-status"
                      checked={form.isactive}
                      onChange={(e) => setForm({ ...form, isactive: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <label htmlFor="user-status" className="text-xs font-bold text-gray-700 cursor-pointer">
                      Account Enabled (Active)
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-7 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow transition flex items-center gap-2"
                >
                  {submitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  <span>{editingUser ? 'Update User' : 'Create User'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: View Details */}
      {viewUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fadeIn">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <h3 className="text-lg font-bold">{viewUser.username}</h3>
              <button onClick={() => setViewUser(null)} className="text-white/80 hover:text-white p-1 rounded-full">
                <FaTimes />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Role</span>
                  <strong className="text-gray-800 text-base">{viewUser.role?.roleName || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Branch</span>
                  <strong className="text-gray-800 text-base">{viewUser.branch?.branchname || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Email</span>
                  <strong className="text-blue-600">{viewUser.email}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Phone</span>
                  <strong className="text-gray-800">{viewUser.phone}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Joining Date</span>
                  <strong className="text-gray-800">{viewUser.dateofjoining ? viewUser.dateofjoining.split('T')[0] : '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Status</span>
                  <span className={`inline-block px-2.5 py-0.5 rounded text-xs font-bold ${viewUser.isactive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {viewUser.isactive ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
              <button onClick={() => setViewUser(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold">
                Close
              </button>
              <button
                onClick={() => {
                  const target = viewUser;
                  setViewUser(null);
                  handleOpenModal(target);
                }}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMaster;